import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {type ReactNode, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

/** Material's arrow_upward / arrow_downward / keyboard_return, on the 0 -960 960 960 grid. */
const GLYPH_UP = "M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z";
const GLYPH_DOWN = "M440-800v487L216-537l-56 57 320 320 320-320-56-57-224 224v-487h-80Z";
const GLYPH_ENTER = "M760-200v-160q0-50-35-85t-85-35H273l144 144-57 56-240-240 240-240 57 56-144 144h367q83 0 141.5 58.5T840-360v160h-80Z";

/** The Enter key's tail points down from the top right, so Material's keyboard_return is flipped vertically. */
const ENTER_TRANSFORM = "scaleY(-1)";

const KeyGlyph = ({path, transform}: {path: string; transform?: string}) => (
    <Box
        component="svg"
        viewBox="0 -960 960 960"
        sx={{width: 16, height: 16, fill: "currentColor", transform}}
    >
        <path d={path}/>
    </Box>
);

const KeyCap = ({children}: {children: ReactNode}) => (
    <Box
        component="kbd"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 26,
            height: 24,
            px: 0.75,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            color: "text.secondary",
        }}
    >
        {typeof children === "string"
            ? <Typography variant="body2" color="inherit">{children}</Typography>
            : children}
    </Box>
);

const Hint = ({caps, action}: {caps: ReactNode[]; action: string}) => (
    <Stack direction="row" alignItems="center" gap={0.75}>
        {caps.map((cap, index) => <KeyCap key={index}>{cap}</KeyCap>)}
        <Typography variant="body2" color="text.secondary">{action}</Typography>
    </Stack>
);

/** The common keyboard legend beneath searchable authoring pickers. */
export function PickerKeyboardHints() {
    const {t} = useTranslation();

    return (
        <>
            <Divider/>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{px: 2, py: 1}}
            >
                <Hint
                    caps={[<KeyGlyph path={GLYPH_UP}/>, <KeyGlyph path={GLYPH_DOWN}/>]}
                    action={t("To navigate")}
                />
                <Stack direction="row" alignItems="center" gap={2}>
                    <Hint
                        caps={[<KeyGlyph path={GLYPH_ENTER} transform={ENTER_TRANSFORM}/>]}
                        action={t("To select")}
                    />
                    <Hint caps={["esc"]} action={t("To close")}/>
                </Stack>
            </Stack>
        </>
    );
}

type PickerNavigationOptions<T> = {
    items: T[];
    onSelect: (item: T) => void;
    /** Changing this resets selection to the first result. */
    resetKey: string;
};

/** Shared pointer and keyboard selection semantics for the variable and emoji pickers. */
export function usePickerNavigation<T>({items, onSelect, resetKey}: PickerNavigationOptions<T>) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLUListElement>(null);
    const cameFromKeyboard = useRef(false);
    const pointerAt = useRef<{x: number; y: number} | null>(null);

    useEffect(() => setSelectedIndex(0), [resetKey]);

    useEffect(() => {
        if (!cameFromKeyboard.current) return;
        listRef.current
            ?.querySelector(`[data-index="${selectedIndex}"]`)
            ?.scrollIntoView({block: "nearest"});
    }, [selectedIndex]);

    useEffect(() => {
        if (selectedIndex < items.length) return;
        setSelectedIndex(Math.max(items.length - 1, 0));
    }, [items.length, selectedIndex]);

    const selectFromPointer = (index: number, x: number, y: number) => {
        const last = pointerAt.current;
        if (last && last.x === x && last.y === y) return;
        pointerAt.current = {x, y};
        cameFromKeyboard.current = false;
        setSelectedIndex(index);
    };

    const selectFromKeyboard = (index: number) => {
        cameFromKeyboard.current = true;
        setSelectedIndex(index);
    };

    const handleKey = (key: string): boolean => {
        if (items.length === 0) return false;
        if (key === "ArrowDown") {
            selectFromKeyboard(Math.min(selectedIndex + 1, items.length - 1));
            return true;
        }
        if (key === "ArrowUp") {
            selectFromKeyboard(Math.max(selectedIndex - 1, 0));
            return true;
        }
        if ((key === "Enter" || key === "Tab") && items[selectedIndex]) {
            onSelect(items[selectedIndex]);
            return true;
        }
        return false;
    };

    return {selectedIndex, listRef, selectFromPointer, handleKey};
}

